// GDPR cookie consent helpers. Shown only to EU/UK/CH visitors, detected
// via IANA timezone. Consent is persisted to localStorage; PostHog will
// not initialize for EU visitors until consent is granted.

export const CONSENT_STORAGE_KEY = "checkbloom_cookie_consent";

export type CookieConsent = {
  analytics: boolean;
  essential: boolean;
  decidedAt: string;
};

// IANA timezone prefixes considered inside GDPR / UK GDPR / Swiss FADP scope.
const EU_TZ_PREFIXES = ["Europe/", "Atlantic/Faroe", "Atlantic/Reykjavik"];

// Non-EEA European timezones we EXCLUDE (Russia, Turkey, Belarus, etc. —
// GDPR does not apply). Kept small; if unsure, we err toward showing the
// banner (better UX than a silent GDPR breach).
const EU_TZ_EXCLUDE = new Set<string>([
  "Europe/Moscow",
  "Europe/Kaliningrad",
  "Europe/Samara",
  "Europe/Volgograd",
  "Europe/Saratov",
  "Europe/Astrakhan",
  "Europe/Ulyanovsk",
  "Europe/Kirov",
  "Europe/Istanbul",
  "Europe/Minsk",
]);

export function detectTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? "";
  } catch {
    return "";
  }
}

export function detectLocale(): string {
  if (typeof navigator === "undefined") return "";
  return navigator.language ?? "";
}

export function isEUVisitor(tz: string = detectTimezone()): boolean {
  if (!tz) return false;
  if (EU_TZ_EXCLUDE.has(tz)) return false;
  return EU_TZ_PREFIXES.some((p) => tz.startsWith(p));
}

// CNIL requires renewing consent at least every 13 months.
const CONSENT_MAX_AGE_MS = 13 * 30 * 24 * 60 * 60 * 1000;

export function readConsent(): CookieConsent | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.analytics !== "boolean") return null;
    const decidedAt = typeof parsed?.decidedAt === "string" ? Date.parse(parsed.decidedAt) : NaN;
    if (!Number.isNaN(decidedAt) && Date.now() - decidedAt > CONSENT_MAX_AGE_MS) {
      try {
        window.localStorage.removeItem(CONSENT_STORAGE_KEY);
      } catch {}
      return null;
    }
    return parsed as CookieConsent;
  } catch {
    return null;
  }
}

// Custom event that the footer link dispatches to re-open the consent banner.
export const REOPEN_CONSENT_EVENT = "checkbloom:reopen-cookie-consent";

export function reopenConsentBanner(): void {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent(REOPEN_CONSENT_EVENT));
  } catch {}
}

// Detect whether the visitor is in the France / CNIL scope so we can enforce
// the stricter button-parity rule on top of the baseline GDPR banner.
export function isFranceVisitor(tz: string = detectTimezone()): boolean {
  return tz === "Europe/Paris";
}

export function writeConsent(analytics: boolean): CookieConsent {
  const record: CookieConsent = {
    analytics,
    essential: true,
    decidedAt: new Date().toISOString(),
  };
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
  } catch {}
  return record;
}

// True when analytics may initialize / capture.
// - Non-EU: always true (no consent required).
// - EU: only if the user has explicitly accepted analytics.
export function analyticsAllowed(): boolean {
  if (typeof window === "undefined") return false;
  if (!isEUVisitor()) return true;
  const c = readConsent();
  return c?.analytics === true;
}

// Anonymous session identifier used only for consent audit rows.
// Not linked to any user id. Regenerated per browser.
const SESSION_KEY = "checkbloom_consent_session";
export function getAnonSessionId(): string {
  if (typeof window === "undefined") return "";
  try {
    let sid = window.localStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = (crypto.randomUUID?.() ?? `s_${Date.now()}_${Math.random().toString(36).slice(2)}`);
      window.localStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return `s_${Date.now()}`;
  }
}
